import { CheckCircle2, XCircle, ShieldAlert } from 'lucide-react'
import { type ReactElement } from 'react'
import { type UseFormRegister, type FieldErrors } from 'react-hook-form'

import { type UserFormInput } from '@/lib/validation/user-schemas'

interface AccountStatusSectionProps {
  register: UseFormRegister<UserFormInput>
  errors: FieldErrors<UserFormInput>
}

export function AccountStatusSection({ register, errors }: AccountStatusSectionProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-6 space-y-6">
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Account Status
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Control account access and verification status
          </p>
        </div>

        <div className="space-y-4">
          <StatusCheckbox
            register={register}
            fieldName="accountStatus.enabled"
            id="enabled"
            icon={<CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />}
            label="Account enabled"
            description="User can log in and access the system"
            errorMessage={errors.accountStatus?.enabled?.message}
          />
          <StatusCheckbox
            register={register}
            fieldName="accountStatus.emailVerified"
            id="emailVerified"
            icon={<CheckCircle2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
            label="Email verified"
            description="Email address has been verified"
            errorMessage={errors.accountStatus?.emailVerified?.message}
          />
          <StatusCheckbox
            register={register}
            fieldName="accountStatus.locked"
            id="locked"
            icon={<ShieldAlert className="w-4 h-4 text-red-600 dark:text-red-400" />}
            label="Account locked"
            description="Prevent login due to security concerns"
            errorMessage={errors.accountStatus?.locked?.message}
          />
          <StatusGuidelines />
        </div>
      </div>
    </div>
  )
}

const StatusCheckbox = ({
  register,
  fieldName,
  id,
  icon,
  label,
  description,
  errorMessage,
}: {
  register: UseFormRegister<UserFormInput>
  fieldName: 'accountStatus.enabled' | 'accountStatus.emailVerified' | 'accountStatus.locked'
  id: string
  icon: ReactElement
  label: string
  description: string
  errorMessage: string | undefined
}) => (
  <>
    <div className="flex items-start">
      <div className="flex items-center h-6">
        <input
          {...register(fieldName)}
          type="checkbox"
          id={id}
          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900"
        />
      </div>
      <div className="ml-3">
        <label
          htmlFor={id}
          className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2"
        >
          {icon}
          {label}
        </label>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
    </div>
    {errorMessage && <p className="ml-7 text-sm text-red-600 dark:text-red-400">{errorMessage}</p>}
  </>
)

const StatusGuidelines = () => (
  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
    <div className="flex items-start gap-3">
      <XCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-blue-800 dark:text-blue-200">
        <p className="font-medium mb-1">Account status guidelines</p>
        <ul className="space-y-1 text-blue-700 dark:text-blue-300">
          <li>Disabled accounts cannot log in but preserve all data and settings</li>
          <li>Locked accounts are typically due to security events or policy violations</li>
          <li>Email verification is recommended but not required for system access</li>
        </ul>
      </div>
    </div>
  </div>
)
